import React from 'react';
import {classes, style} from './doppe-viewer-horizontal-button.st.css';
import type {OmitStrict, Values} from "@wix/devzai-utils-common";
import {HorizontalButton, reactCreateMemo} from "@wix/devzai-utils-react";
import {DoppeViewerSpinner, DoppeViewerSpinnerSize} from "../doppe-viewer-spinner/doppe-viewer-spinner";

export const DoppeViewerHorizontalButtonSize = {
    XS: 'xs',
    Small: 'small',
    MediumSmall: 'mediumSmall',
    Medium: 'medium',
    Large: 'large',
} as const;

export type DoppeViewerHorizontalButtonSize = Values<typeof DoppeViewerHorizontalButtonSize>;

export const DoppeViewerHorizontalButtonPriority = {
    Primary: 'primary',
    Secondary: 'secondary',
} as const;

export type DoppeViewerHorizontalButtonPriority = Values<typeof DoppeViewerHorizontalButtonPriority>;

export const DoppeViewerHorizontalButtonSkin = {
    None: 'none',
    Default: 'default',
} as const;

export type DoppeViewerHorizontalButtonSkin = Values<typeof DoppeViewerHorizontalButtonSkin>;

/**
 * Resolves the button size based on layout size and viewport.
 * 
 * Button Size Logic:
 * - Layout Size: Small → Mobile: XS, Desktop: Small
 * - Layout Size: Medium → Mobile: MediumSmall, Desktop: Medium
 * 
 * @param layoutSize - The grid layout size (Small or Medium)
 * @param isDesktop - Whether the current viewport is desktop (Wide layout)
 * @returns The appropriate button size
 */
export function doppeViewerHorizontalButtonSizeResolveFromLayoutSize(
    layoutSize: 'Small' | 'Medium',
    isDesktop: boolean
): DoppeViewerHorizontalButtonSize {
    if (layoutSize === 'Small') {
        return isDesktop ? DoppeViewerHorizontalButtonSize.Small : DoppeViewerHorizontalButtonSize.XS;
    } else {
        // Medium layout size
        return isDesktop ? DoppeViewerHorizontalButtonSize.Medium : DoppeViewerHorizontalButtonSize.MediumSmall;
    }
}

export const DoppeViewerHorizontalButton = reactCreateMemo(function DoppeViewerHorizontalButton (props: DoppeViewerHorizontalButton.Props) {
    const {
        size = DoppeViewerHorizontalButtonSize.Medium,
        priority = DoppeViewerHorizontalButtonPriority.Primary,
        skin = DoppeViewerHorizontalButtonSkin.None,
        textual = false,
        isLoading = false,

        className,
        ...htmlAttributes
    } = props;

    return (
        <HorizontalButton
            {...htmlAttributes}
            size={size}
            loader={isLoading ? (
                <DoppeViewerSpinner
                    className={style(classes.spinner, {})}
                    size={DoppeViewerSpinnerSize.Tiny}
                />
            ) : undefined}
            className={style(classes.root, {size: size, priority: priority, skin: skin, textual: textual}, className)}
        />
    );
});

export namespace DoppeViewerHorizontalButton {

    export interface Props extends OmitStrict<HorizontalButton.Props<DoppeViewerHorizontalButtonSize>, 'loader' | 'size'> {
        size?: DoppeViewerHorizontalButtonSize;
        /**
         * Priority variant for the button.
         * @default 'primary'
         */
        priority?: DoppeViewerHorizontalButtonPriority;
        /**
         * Skin variant for the button.
         * - None: No styling (only sizing) - for backward compatibility
         * - Default: Full styling with colors, hover states, etc.
         * @default 'none'
         */
        skin?: DoppeViewerHorizontalButtonSkin;
        textual?: boolean;
        /**
         * Whether the button is in a loading state.
         * When true, displays a spinner and disables the button.
         * @default false
         */
        isLoading?: boolean;
    }
}
